<template>
  <div
    id="inspire"
    class="fill-height"
  >
    <v-app-bar data-cy="public-signing-app-bar">
      <v-app-bar-title>SiteLOG</v-app-bar-title>
    </v-app-bar>
    <v-main>
      <v-container max-width="1200">
        <v-stepper
          v-model="step"
          mobile-breakpoint="sm"
          data-cy="public-signing-stepper"
        >
          <v-stepper-header>
            <v-stepper-item
              :value="1"
              data-cy="public-signing-step-review"
            >
              Pregled dokumenta
            </v-stepper-item>
            <v-stepper-item
              :value="2"
              data-cy="public-signing-step-signature"
            >
              Podpis
            </v-stepper-item>
            <v-stepper-item
              :value="3"
              data-cy="public-signing-step-notes"
            >
              Dodajanje opomb
            </v-stepper-item>
            <v-stepper-item
              :value="4"
              data-cy="public-signing-step-finished"
            >
              Zaključek
            </v-stepper-item>
          </v-stepper-header>

          <v-stepper-window>
            <!-- Pregled dokumenta -->
            <v-stepper-window-item :value="1">
              <v-card-actions class="justify-end">
                <v-btn
                  color="primary"
                  variant="outlined"
                  data-cy="public-signing-next-to-signature"
                  @click="openSignatureStep"
                >
                  Naprej
                </v-btn>
              </v-card-actions>

              <PdfPreview :document-url="documentUrl" />
            </v-stepper-window-item>

            <!-- Podpis -->
            <v-stepper-window-item :value="2">
              <div
                v-show="showSignaturePad"
                ref="signatureContainer"
                class="signature-container"
              >
                <p>Lastoročen podpis vnesite v črn okvirček:</p>
                <canvas
                  ref="signatureCanvas"
                  class="signature-canvas"
                  data-cy="public-signature-canvas"
                />
              </div>

              <div
                v-show="!showSignaturePad"
                class="saved-signature-container"
              >
                <img
                  :src="signature"
                  class="saved-signature"
                  alt="Shranjeni podpis"
                  data-cy="public-saved-signature"
                >
              </div>

              <v-card-actions class="justify-end">
                <v-btn
                  v-show="!showSignaturePad"
                  color="primary"
                  variant="text"
                  data-cy="public-create-new-signature"
                  @click="useNewSignature"
                >
                  Ustvari nov podpis
                </v-btn>
                <v-btn
                  color="primary"
                  variant="outlined"
                  data-cy="public-signing-next-to-notes"
                  @click="step = 3"
                >
                  Podpiši
                </v-btn>
              </v-card-actions>
            </v-stepper-window-item>

            <!-- Vnos opomb s strani nadzora -->
            <v-stepper-window-item :value="3">
              <v-textarea
                v-model="notes.dopolnilni_del"
                label="6. Dodatne ugotovitve, navodila in sporočila izvajalcu"
                :messages="siteLog.dopolnilni_del ? ['Prejšnji vnosi: ' + siteLog.dopolnilni_del] : []"
                auto-grow
                data-cy="public-signing-note-op6"
              />

              <v-textarea
                v-model="notes.opombe_in_zahteve_odgovornega_vodje"
                label="6.a Opombe odgovornega vodje projekta"
                :messages="siteLog.opombe_in_zahteve_odgovornega_vodje ? ['Prejšnji vnosi: ' + siteLog.opombe_in_zahteve_odgovornega_vodje] : []"
                auto-grow
                data-cy="public-signing-note-op6a"
              />

              <v-textarea
                v-model="notes.opombe_in_zahteve_inspekcijskih_sluzb"
                label="6.b Opombe inšpekcijskih služb"
                :messages="siteLog.opombe_in_zahteve_inspekcijskih_sluzb ? ['Prejšnji vnosi: ' + siteLog.opombe_in_zahteve_inspekcijskih_sluzb] : []"
                auto-grow
                data-cy="public-signing-note-op6b"
              />

              <v-textarea
                v-model="notes.opombe_in_zahteve_projektantov"
                label="6.c Opombe projektantov in drugih strokovnjakov"
                :messages="siteLog.opombe_in_zahteve_projektantov ? ['Prejšnji vnosi: ' + siteLog.opombe_in_zahteve_projektantov] : []"
                auto-grow
                data-cy="public-signing-note-op6c"
              />

              <v-textarea
                v-model="notes.opombe_in_zahteve_soglasodajalcev"
                label="6.d Opombe soglasodajalcev"
                :messages="siteLog.opombe_in_zahteve_soglasodajalcev ? ['Prejšnji vnosi: ' + siteLog.opombe_in_zahteve_soglasodajalcev] : []"
                auto-grow
                data-cy="public-signing-note-op6d"
              />

              <v-card-actions class="justify-end">
                <v-btn
                  color="primary"
                  variant="outlined"
                  :loading="saving"
                  data-cy="public-signing-submit"
                  @click="previewAtFinalStep"
                >
                  Naprej
                </v-btn>
              </v-card-actions>
            </v-stepper-window-item>

            <!-- Zaključek/pregled -->
            <v-stepper-window-item :value="4">
              <v-alert
                type="success"
                data-cy="public-signing-success"
              >
                Podpisovanje je končano, lahko zaprete okno brskalnika.
              </v-alert>

              <v-card-actions class="justify-end">
                <v-btn
                  color="primary"
                  variant="outlined"
                  data-cy="public-signing-download"
                  @click="downloadSiteLog"
                >
                  Prenos dnevnega lista
                </v-btn>
              </v-card-actions>

              <PdfPreview
                v-if="step == 4"
                :document-url="documentUrl"
              />
            </v-stepper-window-item>
          </v-stepper-window>
        </v-stepper>
      </v-container>
    </v-main>
  </div>
</template>

<script>
  import SignaturePad from 'signature_pad';
  import trimCanvas from 'trim-canvas';
  import PdfPreview from '@/views/components/PdfPreview';
  import axios from 'axios';

  export default {
    name: 'PublicSiteLogSigningView',
    components: { PdfPreview },
    data() {
      return {
        step: 1,
        projectId: null,
        siteLogId: null,
        secret: null,
        siteLog: {
          dopolnilni_del: '',
          opombe_in_zahteve_odgovornega_vodje: '',
          opombe_in_zahteve_inspekcijskih_sluzb: '',
          opombe_in_zahteve_projektantov: '',
          opombe_in_zahteve_soglasodajalcev: '',
        },
        signature: null,
        showSignaturePad: false,
        notes: {
          dopolnilni_del: '',
          opombe_in_zahteve_odgovornega_vodje: '',
          opombe_in_zahteve_inspekcijskih_sluzb: '',
          opombe_in_zahteve_projektantov: '',
          opombe_in_zahteve_soglasodajalcev: '',
          signature_odgovorni: null,
        },
        nadzorniSignaturePad: null,
        documentUrl: null,
        saving: false,

        // Final step data
        currentPage: 1,
        numOfPages: null,
        pdfRef: null
      }
    },
    async created() {
      const requestData = JSON.parse(atob(this.$route.query.d));
      this.projectId = requestData.project_id;
      this.siteLogId = requestData.site_log_id;
      this.secret = requestData.key;
      this.documentUrl = `${this.$config.API_PDF_EXPORT_BASE}/dnevni-list/${this.projectId}/${requestData.date},${requestData.sequence}`;

      const siteLogResponse = (await axios.get(`${this.$config.API_SIGN_SITELOG}?siteLogId=${this.siteLogId}`, {
        headers: {
          Authorization: this.secret
        }
      })).data;

      this.siteLog = siteLogResponse.sitelog;
      this.signature = siteLogResponse.signature;

      this.notes = {
        dopolnilni_del: '',
        opombe_in_zahteve_odgovornega_vodje: '',
        opombe_in_zahteve_inspekcijskih_sluzb: '',
        opombe_in_zahteve_projektantov: '',
        opombe_in_zahteve_soglasodajalcev: '',
      };

      this.showSignaturePad = !(this.signature || '').startsWith('data:image');
    },
    beforeUnmount() {
      if (this.nadzorniSignaturePad) {
        this.nadzorniSignaturePad.off();
      }
    },
    methods: {
      async openSignatureStep() {
        this.step = 2;

        if (this.showSignaturePad) {
          await this.initSignaturePad();
        }
      },
      async useNewSignature() {
        this.showSignaturePad = true;
        await this.initSignaturePad();
      },
      async initSignaturePad() {
        await this.$nextTick();
        await new Promise(resolve => window.requestAnimationFrame(resolve));

        const signatureCanvas = this.$refs.signatureCanvas;
        const signatureContainer = this.$refs.signatureContainer;

        if (!signatureCanvas || !signatureContainer) {
          return;
        }

        if (this.nadzorniSignaturePad) {
          this.nadzorniSignaturePad.off();
        }

        const pixelRatio = Math.max(window.devicePixelRatio || 1, 1);
        const canvasWidth = Math.max(signatureContainer.clientWidth, 1);
        const canvasHeight = Math.min(Math.max(canvasWidth * 0.35, 200), 320);

        signatureCanvas.width = canvasWidth * pixelRatio;
        signatureCanvas.height = canvasHeight * pixelRatio;
        signatureCanvas.style.width = `${canvasWidth}px`;
        signatureCanvas.style.height = `${canvasHeight}px`;
        signatureCanvas.getContext('2d').scale(pixelRatio, pixelRatio);

        this.nadzorniSignaturePad = new SignaturePad(signatureCanvas, {
          backgroundColor: 'rgb(255, 255, 255)' // necessary for saving image as JPEG; can be removed is only saving as PNG or SVG
        });
      },
      async saveAndSign() {
        const hasNewSignature = this.nadzorniSignaturePad && !this.nadzorniSignaturePad.isEmpty();

        if (hasNewSignature) {
          trimCanvas(this.nadzorniSignaturePad.canvas);
        }

        this.notes.signature_odgovorni = hasNewSignature
          ? this.nadzorniSignaturePad.toDataURL('image/png')
          : this.signature;

        this.saving = true;

        const requestBody = {
          signature: this.notes.signature_odgovorni,
          dopolnilni_del: this.notes.dopolnilni_del,
          opombe_in_zahteve_odgovornega_vodje: this.notes.opombe_in_zahteve_odgovornega_vodje,
          opombe_in_zahteve_inspekcijskih_sluzb: this.notes.opombe_in_zahteve_inspekcijskih_sluzb,
          opombe_in_zahteve_projektantov: this.notes.opombe_in_zahteve_projektantov,
          opombe_in_zahteve_soglasodajalcev: this.notes.opombe_in_zahteve_soglasodajalcev,
        }

        try {
          await axios.post(
              `${this.$config.API_SIGN_SITELOG}?siteLogId=${this.siteLogId}`,
              requestBody,
              {
                headers: {
                  Authorization: this.secret
                }
              }
          );
        } finally {
          this.saving = false;
        }
      },
      /**
       * Manipulating PDF on last review step
       */
      async previewAtFinalStep() {
        await this.saveAndSign();
        this.step = 4;
      },
      downloadSiteLog() {
        window.open(this.documentUrl, '_blank');
      },
    }
  }
</script>


<style scoped>
  .signature-container {
    width: 100%;
  }

  .signature-container p {
    margin-bottom: 8px;
  }

  .signature-canvas {
    display: block;
    max-width: 100%;
    border: 1px solid #000;
    touch-action: none;
  }

  .saved-signature-container {
    overflow-x: auto;
  }

  .saved-signature {
    display: block;
    max-width: 100%;
    height: auto;
  }

</style>
